import { useContext, useMemo } from 'react';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { buildLoginErrorLog, type LoginErrorLogFields, type LoginErrorPage, type LoginErrorOutcome } from './login-error-log';

type Logger = {
  // eslint-disable-next-line no-unused-vars
  trace: (message: string) => void;
  // eslint-disable-next-line no-unused-vars
  error: (message: string) => void;
};

const noopLogger: Logger = {
  trace: () => {},
  error: () => {},
};

export type LogLoginErrorEvent = (
  // eslint-disable-next-line no-unused-vars
  page: LoginErrorPage,
  // eslint-disable-next-line no-unused-vars
  outcome: LoginErrorOutcome,
  // eslint-disable-next-line no-unused-vars
  options?: {
    reason?: string;
    email?: string;
    extra?: LoginErrorLogFields['extra'];
    severity?: 'trace' | 'error';
  }
) => void;

// Login-error-page telemetry logger. Reads the shared instrumentation from context (the same
// pipeline that stamps every line with the telemetry session id / `SesID`), creates the
// `pmaxLiteLanding` scenario logger, and returns a `logEvent` function.
//
// The lines follow the shared `LoginErrorIssues::Result page=… outcome=…` schema (see
// login-error-log), mirroring the InvitationIssues pattern so the report infrastructure can parse
// both the same way. Falls back to a no-op logger when instrumentation is unavailable (e.g. tests,
// demos) so callers never need to guard.
//
// NOTE: page-shown / button-click lines are routine analytics and MUST be emitted via `trace`.
// Reserve `severity: 'error'` for lines that represent a broken flow needing investigation, such as
// a server-supplied URL the page cannot function without being absent.
export const useLoginErrorLogger = (): LogLoginErrorEvent => {
  const instrumentation = useContext(InstrumentationContext) as any;

  const logger = useMemo<Logger>(() => {
    if (instrumentation && instrumentation.pmaxLiteLanding) {
      return instrumentation.pmaxLiteLanding.create().logger.create();
    }
    return noopLogger;
  }, [instrumentation]);

  return useMemo<LogLoginErrorEvent>(() => (page, outcome, options = {}) => {
    const line = buildLoginErrorLog({
      page,
      outcome,
      reason: options.reason,
      email: options.email,
      extra: options.extra,
    });

    if (options.severity === 'error') {
      logger.error(line);
    } else {
      logger.trace(line);
    }
  }, [logger]);
};
